import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { action, computed, observable, makeObservable } from 'mobx';
import {
  dismissablePromiseLoader,
  logger,
  PromiseState,
  PromiseThrottler,
  watchPromise
} from 'lib/helpers/index';
import { HomeworkHubMessage, HomeworkRole } from '../../homework/homework-models';

export class ExamJoinStore {
  @observable.ref
  state: PromiseState<HomeworkHubMessage> | null;

  requestCount: number = 0;

  private readonly throttler: PromiseThrottler;

  constructor(readonly examId: number,
    readonly homeworkRole: HomeworkRole) {
    makeObservable(this);
    this.state = null;
    this.throttler = new PromiseThrottler(1000, 3);
  }

  @computed
  get isPending(): boolean {
    return this.state?.isPending === true;
  }

  @computed
  get isRejected(): boolean {
    return this.state?.isRejected === true;
  }

  @computed
  get isResolved(): boolean {
    return this.state?.isResolved === true;
  }

  @computed
  get joined(): boolean {
    if (this.state === null || !this.state.isResolved)
      return false;
    let message = this.state.value;
    return (message.MessageType === 'exam-joined');
  }

  @action
  join(connection: HubConnection) {
    if (connection.state !== HubConnectionState.Connected) {
      this.reset();
      return;
    }

    const state = this.state;

    if (state !== null && (state.isPending || state.isResolved))
      return;

    let loader: () => Promise<HomeworkHubMessage>;
    if (this.homeworkRole === 'Student')
      loader = () => connection.invoke<HomeworkHubMessage>('joinAsStudent', this.examId);
    else if (this.homeworkRole === 'Teacher')
      loader = () => connection.invoke<HomeworkHubMessage>('joinAsTeacher', this.examId);
    else
      loader = () => Promise.reject('ExamJoinStore: Invalid exam role');

    this.requestCount++;
    let requestCount = this.requestCount;

    let dismissible = dismissablePromiseLoader(loader,
      () => requestCount !== this.requestCount);
    let promise = this.throttler.run(dismissible);
    promise.catch((error) => {
      logger.log('ExamJoinStore.error', error);
    });

    this.watchPromise(promise);
  }

  @action
  reset = () => {
    this.requestCount++;
    this.watchPromise(null);
  };

  @action
  private watchPromise = (promise: Promise<HomeworkHubMessage> | null) => {
    if (promise === null)
      this.state = null;
    else
      this.state = watchPromise(promise);
  };
}
